import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Keyboard, Play } from "lucide-react";
import { api } from "@/lib/api";
import { deskApi } from "@/lib/deskApi";
import { apiError } from "@/lib/queryState";
import { RUN_META } from "@/lib/format";
import { CheckpointTime } from "@/components/CheckpointTime";
import { Badge, Button, Icon, Kbd, Modal, Panel, SystemBadge, etAndLocal } from "@/sentinel";

const sentence = (s) => { const t = String(s || "").toLowerCase(); return t.charAt(0).toUpperCase() + t.slice(1); };

/**
 * Owner/analyst run controls for the desk: Start live, run 1–6, RECALC. Every enqueue and RECALC
 * (button or keyboard 1–6 / R) goes through a confirmation dialog. A 409 RUN_TOO_LATE / RUN_TOO_EARLY
 * is explained in the dialog; an owner may then run anyway, tagged LATE (force_late=true).
 * Rendered only for analysts and owners — viewers never see disabled run buttons.
 */
export function RunControls({ date, doneRuns, latestRun, blocked, blockedReason, isOwner, risk, onChanged, onStartedLive, keyboard = true }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recalcRun, setRecalcRun] = useState(null);
  const [pending, setPending] = useState(null); // { kind: "enqueue" | "recalc", run, error? }
  const pendingRef = useRef(null);
  pendingRef.current = pending;
  const target = recalcRun ?? latestRun;
  const sealed = [...(doneRuns || [])].sort((a, b) => a - b);

  const ask = (kind, run) => { if (!blocked && !busy) setPending({ kind, run }); };

  useEffect(() => {
    if (!keyboard || blocked) return undefined;
    const onKey = (e) => {
      if (pendingRef.current || busy) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "select" || tag === "textarea" || e.target?.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector(".sn-modal")) return;
      if (e.key >= "1" && e.key <= "6") { e.preventDefault(); ask("enqueue", Number(e.key)); }
      else if ((e.key === "r" || e.key === "R") && target) { e.preventDefault(); ask("recalc", target); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboard, blocked, busy, target, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const execute = async ({ forceLate = false } = {}) => {
    const p = pending;
    if (!p) return;
    setBusy(true);
    try {
      const res = p.kind === "recalc" ? await deskApi.recalc(date, p.run) : await deskApi.enqueue(date, p.run, { forceLate });
      if (res?.idempotent) toast.info(`Run ${p.run} already exists — no duplicate created.`);
      else toast.success(p.kind === "recalc" ? `RECALC appended for Run ${p.run}.` : `Run ${p.run} queued${forceLate ? " · tagged LATE" : ""}.`);
      setPending(null);
      onChanged?.();
    } catch (e) {
      const err = apiError(e);
      if (err?.status === 409 && (err.code === "RUN_TOO_LATE" || err.code === "RUN_TOO_EARLY")) {
        setPending({ ...p, error: { ...err, detail: e.response?.data?.detail || {} } });
      } else {
        toast.error(err?.message || "The request failed.");
        setPending(null);
      }
    } finally {
      setBusy(false);
    }
  };

  const startLive = async () => {
    setBusy(true);
    try {
      const s = await api.startLive();
      toast.success(`Live session started for ${s.session_date}.`);
      onStartedLive?.(s.session_date);
    } catch (e) {
      toast.error(apiError(e)?.message || "Could not start the live session.");
    } finally {
      setBusy(false);
    }
  };

  const err = pending?.error;
  const cutoff = err?.detail?.cutoff_et || RUN_META[pending?.run]?.time;
  const dialogTitle = !pending ? "" : err
    ? (err.code === "RUN_TOO_LATE" ? `Run ${pending.run} is past its window` : `Run ${pending.run} is not open yet`)
    : pending.kind === "recalc" ? `Append a RECALC for Run ${pending.run}?` : `Queue Run ${pending.run} now?`;

  return (
    <Panel title="Run controls" icon="SlidersHorizontal" data-testid="run-controls"
      meta={
        <>
          {risk?.config?.enabled && (risk?.tripped
            ? <SystemBadge kind="job-failed" data-testid="guardrail-chip" title={risk.tripped.reason}>Guardrails tripped · KS-07</SystemBadge>
            : <Badge icon="Shield" data-testid="guardrail-chip" title="The desk halts automatically on a P&L breach">Guardrails armed</Badge>)}
        </>
      }
      actions={
        <Button size="sm" variant="ghost" icon={open ? "ChevronUp" : "ChevronDown"} aria-expanded={open} aria-controls="run-controls-body"
          onClick={() => setOpen((o) => !o)} data-testid="run-controls-toggle">{open ? "Hide" : "Show"}</Button>
      }>
      <div className="sn-stack">
        <div className="sn-row" style={{ gap: "var(--space-3)" }}>
          <Button variant="secondary" icon="Radio" disabled={busy} onClick={startLive} data-testid="start-live-btn" title="Open a live session on the latest trading day">Start live</Button>
          {blocked
            ? <span className="sn-small sn-faint">{blockedReason}</span>
            : keyboard && (
              <span className="sn-row sn-small sn-faint" style={{ gap: 6 }} data-testid="keyboard-hint">
                <Icon name={Keyboard} size={14} /> <Kbd>1</Kbd>–<Kbd>6</Kbd> queue a run · <Kbd>R</Kbd> RECALC · each asks first
              </span>
            )}
        </div>

        {open && !blocked && (
          <div className="sn-stack" id="run-controls-body">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "var(--space-2)" }}>
              {[1, 2, 3, 4, 5, 6].map((n) => {
                const done = doneRuns?.has(n);
                return (
                  <button key={n} type="button" className="sn-btn sn-btn-secondary" disabled={busy} onClick={() => ask("enqueue", n)} data-testid={`run-btn-${n}`}
                    aria-label={`Queue Run ${n}, ${sentence(RUN_META[n].name)}, ${etAndLocal(RUN_META[n].time)}${done ? ", already sealed" : ""}`}
                    style={{ height: "auto", minWidth: 0, padding: "var(--space-2) var(--space-3)", flexDirection: "column", alignItems: "flex-start", gap: 2, whiteSpace: "normal", textAlign: "left" }}>
                    <span className="sn-row" style={{ gap: 6, flexWrap: "nowrap" }}>
                      <Icon name={done ? "Lock" : "Circle"} size={14} style={{ color: "var(--text-tertiary)" }} />
                      <span style={{ fontWeight: 600 }}>Run {n}</span>
                      {done && <span className="sn-small sn-faint">sealed</span>}
                    </span>
                    <CheckpointTime sessionDate={date} cutoffET={RUN_META[n].time} testId={`run-time-${n}`} />
                    <span className="sn-small sn-faint">{n === 6 ? "Post-close review" : sentence(RUN_META[n].name)}</span>
                  </button>
                );
              })}
            </div>
            {sealed.length > 0 && (
              <div className="sn-row" style={{ gap: "var(--space-3)", alignItems: "flex-end" }}>
                <label className="sn-field">
                  <span className="sn-field-label">RECALC target</span>
                  <span className="sn-input-wrap" style={{ width: 120 }}>
                    <select className="sn-input" value={target ?? ""} onChange={(e) => setRecalcRun(Number(e.target.value))} data-testid="recalc-run-select" style={{ cursor: "pointer" }}>
                      {sealed.map((n) => <option key={n} value={n}>Run {n}</option>)}
                    </select>
                  </span>
                </label>
                <Button icon="RotateCw" disabled={busy || !target} onClick={() => ask("recalc", target)} data-testid="recalc-btn">Append RECALC…</Button>
                <span className="sn-small sn-faint">Adds a sibling; the original stays frozen.</span>
              </div>
            )}
          </div>
        )}
      </div>

      {pending && (
        <Modal onClose={() => !busy && setPending(null)} width={480} icon={err ? "Clock" : pending.kind === "recalc" ? "RotateCw" : Play} title={dialogTitle}
          data-testid="run-confirm-dialog"
          footer={
            <>
              <Button variant="ghost" onClick={() => setPending(null)} disabled={busy} data-testid="run-confirm-cancel">Cancel</Button>
              {!err && (
                <Button variant="primary" loading={busy} onClick={() => execute()} data-testid="run-confirm-ok">
                  {pending.kind === "recalc" ? "Append RECALC" : `Queue Run ${pending.run}`}
                </Button>
              )}
              {err?.code === "RUN_TOO_LATE" && isOwner && (
                <Button variant="primary" icon="Clock" loading={busy} onClick={() => execute({ forceLate: true })} data-testid="run-force-late">Run anyway (tagged LATE)</Button>
              )}
            </>
          }>
          {err ? (
            <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="run-window-error">
              <p style={{ margin: 0 }}>{err.message || (err.code === "RUN_TOO_LATE" ? "This run's window has closed." : "This run's window has not opened.")}</p>
              <p className="sn-small sn-muted" style={{ margin: 0 }}>
                Scheduled cutoff <span className="sn-num">{etAndLocal(cutoff)}</span>
                {err.detail?.allowed_until_et ? <> · accepted until <span className="sn-num">{etAndLocal(err.detail.allowed_until_et)}</span></> : null}.
                {err.code === "RUN_TOO_LATE" && (isOwner ? " A late run is recorded with a LATE tag and left out of ENTER, calibration and scoring." : " Only an owner can record a late run.")}
              </p>
            </div>
          ) : (
            <p style={{ margin: 0 }} data-testid="run-confirm-body">
              {pending.kind === "recalc"
                ? <>Re-evaluates Run {pending.run} for <span className="sn-num">{date}</span> and saves a second copy marked RECALC. The original run is not changed.</>
                : <>Starts Run {pending.run} ({n6(pending.run)}) for <span className="sn-num">{date}</span> with the data available now.{doneRuns?.has(pending.run) ? " It is already sealed, so the server will return the existing run." : ""}</>}
            </p>
          )}
        </Modal>
      )}
    </Panel>
  );
}

const n6 = (run) => (run === 6 ? "post-close review" : `${sentence(RUN_META[run]?.name)} · ${etAndLocal(RUN_META[run]?.time)}`);
